import { Injectable } from '@angular/core';
import { HttpUtil } from '@shared/util/http.util';
import { Observable } from 'rxjs';
import { ApplicationConfig } from '@config/application.config';
import { PageDTO } from '@data/common/pagination/PageDTO';
import { PatientNoteDTO } from 'app/view/journal/data/PatientNoteDTO';
import { map } from 'rxjs/operators';
import { ObjectUtil } from '@shared/util/object.util';
import { PatientNotePageCriteriaDTO } from 'app/view/journal/data/PatientNotePageCriteriaDTO';
import { PatientNoteCalendarRequestDTO } from 'app/view/journal/data/PatientNoteCalendarRequestDTO';
import { PatientNoteCalendarDayDTO } from 'app/view/journal/data/PatientNoteDalendarDayDTO';

@Injectable({
  providedIn: 'root'
})
export class CurrentPatientDailyNotesApiService {
  constructor(private readonly httpUtil: HttpUtil) {
  }

  public getPage(criteria: PatientNotePageCriteriaDTO): Observable<PageDTO<PatientNoteDTO>> {
    return this.httpUtil.get(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes/page`, criteria)
      .pipe(
        map(response => ObjectUtil.plainToInstanceFromExist(new PageDTO<PatientNoteDTO>(PatientNoteDTO), response))
      );
  }

  public getCurrentNote(): Observable<PatientNoteDTO> {
    return this.httpUtil.get(
      `${ ApplicationConfig.apiUrl }/patients/me/patient-notes/today`,
      undefined,
      { ignoreError: true }
    ).pipe(
      map((response: PatientNoteDTO) =>
        ObjectUtil.plainToInstance(PatientNoteDTO, response)
      )
    );
  }

  public create(patientNote: PatientNoteDTO): Observable<PatientNoteDTO> {
    const formData = this.convertToFormData(patientNote);

    return this.httpUtil.post(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes`, formData)
      .pipe(
        map((response: PatientNoteDTO) => ObjectUtil.plainToInstance(PatientNoteDTO, response))
      );
  }

  public update(patientNote: PatientNoteDTO): Observable<PatientNoteDTO> {
    const formData = this.convertToFormData(patientNote);

    return this.httpUtil.put(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes/${patientNote.id}`, formData)
      .pipe(
        map((response: PatientNoteDTO) => ObjectUtil.plainToInstance(PatientNoteDTO, response))
      );
  }

  private convertToFormData(patientNote: PatientNoteDTO): FormData {
    const formData = new FormData();
    
    if (patientNote.text) {
      formData.append('text', patientNote.text);
    }
    
    if (patientNote.date) {
      formData.append('date', patientNote.date.toISOString());
    }
    
    if (patientNote.id) {
      formData.append('id', patientNote.id.toString());
    }
    
    if (patientNote.images?.length) {
      patientNote.images.forEach((image) => {
        if (image instanceof File) {
          formData.append('images', image, image.name);
        } else {
          formData.append('imageToPersistIdIn', image.id.toString());
        }
      });
    }
    
    return formData;
  }

  public getPatientNoteById(patientNoteId: number): Observable<PatientNoteDTO> {
    return this.httpUtil.get(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes/${patientNoteId}`)
      .pipe(
        map((response: PatientNoteDTO) =>
          ObjectUtil.plainToInstance(PatientNoteDTO, response)
        )
      );
  }

  public delete(patientNoteId: number): Observable<void> {
    return this.httpUtil.delete(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes/${patientNoteId}`);
  }

  public getCalendarDays(requestDto: PatientNoteCalendarRequestDTO): Observable<PatientNoteCalendarDayDTO> {
    return this.httpUtil.get(`${ ApplicationConfig.apiUrl }/patients/me/patient-notes/calendar`, requestDto)
      .pipe(
        map((response: PatientNoteCalendarDayDTO) =>
          ObjectUtil.plainToInstance(PatientNoteCalendarDayDTO, response)
        )
      );
  }
}
